// @ts-nocheck
// Basic Three.js-based replacement for Shopify's native <model-viewer>.
// Only ever loaded when a product actually has 3D model media - see the
// conditional <script> tag in blocks/product-media.liquid. Three.js
// itself is never fetched/init'd on pages without a model.
//
// No AR support (not needed - handled by Shopify's native viewer
// previously, dropped along with it). Not yet model/texture swapping or
// animations.
//
// PBR lighting: real IBL via a loaded .hdr (data-environment-src, resolved
// in Liquid - the site-wide default baked into assets/, optionally
// overridden per-product via a metaobject/metafield - see
// blocks/product-media.liquid). The synthetic RoomEnvironment is now only
// a defensive fallback for when that's missing or fails to load, not the
// primary path.
import * as THREE from './three.module.min.js';
import { OrbitControls } from './three-orbit-controls.js';
import { GLTFLoader } from './three-gltf-loader.js';
import { RGBELoader } from './three-rgbe-loader.js';
import { RoomEnvironment } from './three-room-environment.js';

class ThreeModelViewer extends HTMLElement {
  connectedCallback() {
    this.posterButton = this.querySelector('.deferred-media__poster-button');
    this.canvasWrapper = this.querySelector('.three-model-canvas-wrapper');
    this.loaded = false;

    if (this.posterButton) {
      this.posterButton.addEventListener('click', () => this.load());
    }
  }

  disconnectedCallback() {
    this.#teardown();
  }

  // Kept as a public method (rather than private) so a future "poster ->
  // click to load" trigger elsewhere (e.g. a thumbnail) could call it too.
  load() {
    if (this.loaded) return;

    const glbSrc = this.dataset.glbSrc;
    if (!glbSrc || !this.canvasWrapper) return;

    this.loaded = true;
    this.posterButton.hidden = true;
    this.canvasWrapper.hidden = false;
    this.classList.add('is-loading');

    this.#initScene(glbSrc, this.dataset.environmentSrc);
  }

  #initScene(glbSrc, environmentSrc) {
    const wrapper = this.canvasWrapper;
    const width = wrapper.clientWidth || this.clientWidth || 1;
    const height = wrapper.clientHeight || this.clientHeight || 1;

    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(0, 0.4, 3);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(width, height);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    // Filmic tone mapping is what keeps PBR materials from looking blown
    // out / plasticky under environment lighting - standard choice for
    // product-style viewers.
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1;
    wrapper.appendChild(renderer.domElement);

    this.#buildEnvironment(scene, renderer, environmentSrc);

    // A single soft light on top of the environment map for a bit of
    // extra highlight/shape definition - the environment map is doing
    // the actual PBR lighting/reflections.
    const keyLight = new THREE.DirectionalLight(0xffffff, 0.6);
    keyLight.position.set(5, 10, 7.5);
    scene.add(keyLight);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.autoRotate = this.dataset.autoRotate === 'true';
    controls.autoRotateSpeed = 0.55;
    controls.enablePan = false;
    controls.minDistance = 0.5;
    controls.maxDistance = 10;
    // Keep the camera from swinging fully underneath the model - a
    // near-full range still allows looking down/up at it without the
    // "upside down" feeling that a completely free polar angle gives.
    controls.maxPolarAngle = Math.PI * 0.85;

    const loader = new GLTFLoader();
    loader.load(
      glbSrc,
      gltf => {
        const model = gltf.scene;

        // Center the model on the origin, regardless of how the source
        // file itself was authored/exported (pivot offset varies a lot in
        // practice) - leave its actual scale/units alone, since we frame
        // the camera to the model below rather than resizing the model to
        // the camera.
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        model.position.sub(center);

        scene.add(model);
        this.#frameCamera(camera, controls, size);
        this.classList.remove('is-loading');
        this.dispatchEvent(new CustomEvent('model-viewer:load'));
      },
      undefined,
      error => {
        this.classList.remove('is-loading');
        this.classList.add('has-error');
        // eslint-disable-next-line no-console
        console.error('[three-model-viewer] Failed to load model:', glbSrc, error);
      }
    );

    this.resizeObserver = new ResizeObserver(() => {
      const w = wrapper.clientWidth;
      const h = wrapper.clientHeight;
      if (!w || !h) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    });
    this.resizeObserver.observe(wrapper);

    const animate = () => {
      this.animationFrame = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    this.renderer = renderer;
    this.scene = scene;
  }

  // Positions the camera so the model fills the frame the same way the
  // poster image did, instead of resizing the model to a fixed scale -
  // works consistently regardless of the source file's own units/scale.
  // Model is assumed already recentered on the origin.
  #frameCamera(camera, controls, size) {
    const vFov = (camera.fov * Math.PI) / 180;
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * camera.aspect);

    const paddingFactor = 1.35; // leaves a bit of breathing room around the model
    const distanceForHeight = size.y / 2 / Math.tan(vFov / 2);
    const distanceForWidth = size.x / 2 / Math.tan(hFov / 2);
    const distance = Math.max(distanceForHeight, distanceForWidth, size.z) * paddingFactor;

    const direction = new THREE.Vector3(0.35, 0.2, 0.9).normalize();
    camera.position.copy(direction.multiplyScalar(distance));
    camera.near = distance / 100;
    camera.far = distance * 100;
    camera.updateProjectionMatrix();

    controls.target.set(0, 0, 0);
    controls.minDistance = distance / 10;
    controls.maxDistance = distance * 4;
    controls.update();
  }

  // Loads the real IBL/HDR environment when one's available, giving PBR
  // materials realistic reflections/ambient lighting; falls back to a
  // synthetic room environment (flatter, but never fails/blocks on
  // network) when no URL is provided or the load errors out.
  #buildEnvironment(scene, renderer, environmentSrc) {
    const pmremGenerator = new THREE.PMREMGenerator(renderer);

    const useSyntheticFallback = () => {
      const environment = pmremGenerator.fromScene(new RoomEnvironment(), 0.04);
      scene.environment = environment.texture;
      pmremGenerator.dispose();
    };

    if (!environmentSrc) {
      useSyntheticFallback();
      return;
    }

    pmremGenerator.compileEquirectangularShader();

    new RGBELoader().load(
      environmentSrc,
      texture => {
        const environment = pmremGenerator.fromEquirectangular(texture);
        scene.environment = environment.texture;
        texture.dispose();
        pmremGenerator.dispose();
      },
      undefined,
      error => {
        // eslint-disable-next-line no-console
        console.error('[three-model-viewer] Failed to load HDR environment, falling back to synthetic room:', environmentSrc, error);
        useSyntheticFallback();
      }
    );
  }

  #teardown() {
    if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
    if (this.resizeObserver) this.resizeObserver.disconnect();

    if (this.scene) {
      this.scene.environment?.dispose?.();
      this.scene.traverse(node => {
        node.geometry?.dispose?.();
        const materials = Array.isArray(node.material) ? node.material : [node.material];
        materials.forEach(material => material?.dispose?.());
      });
    }

    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.domElement.remove();
    }
  }
}

if (!customElements.get('three-model-viewer')) {
  customElements.define('three-model-viewer', ThreeModelViewer);
}
